import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axiosConfig from '../../../../api/axiosConfig';
import * as XLSX from 'xlsx';
import { FiDownload, FiEye, FiPrinter, FiSearch } from 'react-icons/fi';
import TeacherDetailModal from './components/TeacherDetailModal';

const MONTH_OPTIONS = [
  { value: 1, label: 'Ocak' },
  { value: 2, label: 'Şubat' },
  { value: 3, label: 'Mart' },
  { value: 4, label: 'Nisan' },
  { value: 5, label: 'Mayis' },
  { value: 6, label: 'Haziran' },
  { value: 7, label: 'Temmuz' },
  { value: 8, label: 'Ağustos' },
  { value: 9, label: 'Eylül' },
  { value: 10, label: 'Ekim' },
  { value: 11, label: 'Kasım' },
  { value: 12, label: 'Aralık' }
];

const TeacherReportPage = () => {
  const today = useMemo(() => new Date(), []);
  const currentMonth = today.getMonth() + 1;
  const currentYear = today.getFullYear();

  const [teachers, setTeachers] = useState([]);
  const [financialData, setFinancialData] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [periodFilter, setPeriodFilter] = useState('monthly');
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const yearOptions = useMemo(() => {
    return Array.from({ length: 8 }, (_, index) => currentYear - 3 + index);
  }, [currentYear]);

  const requestParams = useMemo(() => {
    const params = { period: periodFilter };

    if (periodFilter === 'monthly') {
      params.month = selectedMonth;
      params.year = selectedYear;
    }

    if (periodFilter === 'yearly') {
      params.year = selectedYear;
    }

    return params;
  }, [periodFilter, selectedMonth, selectedYear]);

  const safeParseFloat = (value) => {
    if (value === null || value === undefined || value === '') return 0;

    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const cleaned = value.replace(/[^0-9.-]/g, '');
      const parsed = Number.parseFloat(cleaned);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    return 0;
  };

  const isTeacherActive = (teacher) => {
    const status = String(teacher?.status ?? '').toLowerCase();
    return status === '1' || status === 'aktif' || status === 'active';
  };

  const fetchTeachersAndFinancials = useCallback(async () => {
    setLoading(true);

    try {
      const teacherRes = await axiosConfig.get('/admin/teachers');
      const allTeachers = Array.isArray(teacherRes.data)
        ? teacherRes.data
        : (teacherRes.data?.data || []);

      const activeTeachers = allTeachers.filter(isTeacherActive);

      try {
        const bulkRes = await axiosConfig.get('/admin/teachers/reports/all-earnings-by-period', {
          params: requestParams
        });

        const bulkData = Array.isArray(bulkRes.data) ? bulkRes.data : [];
        const financials = {};

        bulkData.forEach((item) => {
          financials[item.id] = {
            brutToplam: safeParseFloat(item.brutToplam),
            toplamAvans: Math.abs(safeParseFloat(item.toplamAvans)),
            netToplam: safeParseFloat(item.netToplam),
            dersSayisi: Number(item.dersSayisi) || 0
          };
        });

        activeTeachers.forEach((teacher) => {
          if (!financials[teacher.id]) {
            financials[teacher.id] = {
              brutToplam: 0,
              toplamAvans: 0,
              netToplam: 0,
              dersSayisi: 0
            };
          }
        });

        setTeachers(activeTeachers);
        setFinancialData(financials);
        return;
      } catch (bulkError) {
        console.warn('Bulk endpoint failed, using per-teacher fallback:', bulkError);
      }

      const financialEntries = await Promise.all(
        activeTeachers.map(async (teacher) => {
          try {
            const response = await axiosConfig.get(`/admin/teachers/${teacher.id}/earnings-by-period`, {
              params: requestParams
            });

            return [teacher.id, {
              brutToplam: safeParseFloat(response.data.brutToplam),
              toplamAvans: Math.abs(safeParseFloat(response.data.toplamAvans)),
              netToplam: safeParseFloat(response.data.netToplam),
              dersSayisi: Number(response.data.dersSayisi) || 0
            }];
          } catch (error) {
            console.error(`Teacher ${teacher.id} financial fallback failed:`, error);
            return [teacher.id, {
              brutToplam: 0,
              toplamAvans: 0,
              netToplam: 0,
              dersSayisi: 0
            }];
          }
        })
      );

      setTeachers(activeTeachers);
      setFinancialData(Object.fromEntries(financialEntries));
    } catch (error) {
      console.error('Teacher report data could not be loaded:', error);
      setTeachers([]);
      setFinancialData({});
    } finally {
      setLoading(false);
    }
  }, [requestParams]);

  useEffect(() => {
    fetchTeachersAndFinancials();
  }, [fetchTeachersAndFinancials]);

  const filteredTeachers = useMemo(() => {
    return teachers.filter((teacher) => {
      if (!searchTerm) return true;

      const searchLower = searchTerm.toLowerCase();
      return (
        teacher.adi?.toLowerCase().includes(searchLower) ||
        teacher.telefon?.includes(searchTerm) ||
        teacher.e_posta?.toLowerCase().includes(searchLower)
      );
    });
  }, [teachers, searchTerm]);

  const totals = useMemo(() => {
    return filteredTeachers.reduce((acc, teacher) => {
      const data = financialData[teacher.id] || {};
      return {
        brutToplam: acc.brutToplam + (data.brutToplam || 0),
        toplamAvans: acc.toplamAvans + (data.toplamAvans || 0),
        netToplam: acc.netToplam + (data.netToplam || 0),
        dersSayisi: acc.dersSayisi + (data.dersSayisi || 0)
      };
    }, { brutToplam: 0, toplamAvans: 0, netToplam: 0, dersSayisi: 0 });
  }, [filteredTeachers, financialData]);

  const formatMoney = (amount) => {
    const num = safeParseFloat(amount);
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(num);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getDateRangeLabel = () => {
    const now = new Date();

    if (periodFilter === 'daily') {
      return formatDate(now);
    }

    if (periodFilter === 'weekly') {
      const todayDate = new Date();
      const dayOfWeek = todayDate.getDay();
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

      const startDate = new Date(todayDate);
      startDate.setDate(todayDate.getDate() - diff);

      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);

      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }

    if (periodFilter === 'monthly') {
      const selectedMonthLabel = MONTH_OPTIONS.find((month) => month.value === Number(selectedMonth))?.label || '';
      const isCurrentSelected = Number(selectedMonth) === currentMonth && Number(selectedYear) === currentYear;

      if (isCurrentSelected) {
        return `${selectedMonthLabel} ${selectedYear} - ${formatDate(now)}`;
      }

      const monthEnd = new Date(Number(selectedYear), Number(selectedMonth), 0);
      return `${selectedMonthLabel} ${selectedYear} - ${formatDate(monthEnd)}`;
    }

    if (periodFilter === 'yearly') {
      return `${selectedYear}`;
    }

    return '';
  };

  const getPeriodTag = () => {
    if (periodFilter === 'daily') return 'Günlük';
    if (periodFilter === 'weekly') return 'Haftalık';
    if (periodFilter === 'monthly') return 'Aylık';
    return 'Yıllık';
  };

  const exportToExcel = () => {
    const excelData = filteredTeachers.map((teacher, index) => {
      const data = financialData[teacher.id] || {};
      return {
        '#': index + 1,
        'Ogretmen Adi': teacher.adi,
        Telefon: teacher.telefon || '-',
        'E-posta': teacher.e_posta || '-',
        Ders: data.dersSayisi || 0,
        'Brut Ucret (TL)': data.brutToplam || 0,
        'Avans (TL)': data.toplamAvans || 0,
        'Net Ucret (TL)': data.netToplam || 0
      };
    });

    excelData.push({});
    excelData.push({
      '#': '',
      'Ogretmen Adi': `TOPLAM (${filteredTeachers.length})`,
      Telefon: '',
      'E-posta': '',
      Ders: totals.dersSayisi,
      'Brut Ucret (TL)': totals.brutToplam,
      'Avans (TL)': totals.toplamAvans,
      'Net Ucret (TL)': totals.netToplam
    });

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ogretmen Raporu');

    const fileName = `Ogretmen_Ucret_Raporu_${periodFilter}_${Date.now()}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSetCurrentMonth = () => {
    setSelectedMonth(currentMonth);
    setSelectedYear(currentYear);
  };

  const handleSetCurrentYear = () => {
    setSelectedYear(currentYear);
  };

  const openTeacherDetail = (teacher) => {
    setSelectedTeacher(teacher);
    setIsDetailModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6 print:bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6 print:shadow-none print:border print:border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 tracking-tight">Öğretmen Ücret Raporu</h1>
              <p className="text-gray-500 mt-1">{getDateRangeLabel()}</p>
            </div>
            <div className="flex items-center gap-3 print:hidden">
              <button
                onClick={handlePrint}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              >
                <FiPrinter className="w-4 h-4" />
                Yazdır
              </button>
              <button
                onClick={exportToExcel}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
              >
                <FiDownload className="w-4 h-4" />
                Excel İndir
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 mb-6 print:hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Tarih Aralığı</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <button
                  onClick={() => setPeriodFilter('daily')}
                  className={`px-3 py-2 rounded-lg font-semibold transition-colors ${periodFilter === 'daily' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Günlük
                </button>
                <button
                  onClick={() => setPeriodFilter('weekly')}
                  className={`px-3 py-2 rounded-lg font-semibold transition-colors ${periodFilter === 'weekly' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Haftalık
                </button>
                <button
                  onClick={() => setPeriodFilter('monthly')}
                  className={`px-3 py-2 rounded-lg font-semibold transition-colors ${periodFilter === 'monthly' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Aylık
                </button>
                <button
                  onClick={() => setPeriodFilter('yearly')}
                  className={`px-3 py-2 rounded-lg font-semibold transition-colors ${periodFilter === 'yearly' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Yıllık
                </button>
              </div>

              {(periodFilter === 'monthly' || periodFilter === 'yearly') && (
                <div className="mt-3 bg-slate-100 rounded-lg p-3 flex flex-wrap items-center gap-3">
                  <span className="text-sm font-semibold text-gray-700">{periodFilter === 'monthly' ? 'Ay/Yıl Seç:' : 'Yıl Seç:'}</span>

                  {periodFilter === 'monthly' && (
                    <select
                      value={selectedMonth}
                      onChange={(event) => setSelectedMonth(Number(event.target.value))}
                      className="px-3 py-2 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {MONTH_OPTIONS.map((month) => (
                        <option key={month.value} value={month.value}>{month.label}</option>
                      ))}
                    </select>
                  )}

                  <select
                    value={selectedYear}
                    onChange={(event) => setSelectedYear(Number(event.target.value))}
                    className="px-3 py-2 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>

                  {periodFilter === 'monthly' ? (
                    <button
                      onClick={handleSetCurrentMonth}
                      className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                    >
                      Bu Ay
                    </button>
                  ) : (
                    <button
                      onClick={handleSetCurrentYear}
                      className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                    >
                      Bu Yıl
                    </button>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Öğretmen Ara</label>
              <div className="relative">
                <FiSearch className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Ad, telefon veya e-posta ile ara..."
                  className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm p-5">
            <div className="text-sm text-gray-500">Öğretmen</div>
            <div className="text-4xl font-bold text-gray-900 mt-1">{filteredTeachers.length}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-5">
            <div className="text-sm text-gray-500">Toplam Ders</div>
            <div className="text-4xl font-bold text-violet-600 mt-1">{totals.dersSayisi}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-5">
            <div className="text-sm text-gray-500">Brüt Ücret</div>
            <div className="text-4xl font-bold text-blue-600 mt-1">₺{formatMoney(totals.brutToplam)}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-5">
            <div className="text-sm text-gray-500">Avans</div>
            <div className="text-4xl font-bold text-orange-600 mt-1">₺{formatMoney(totals.toplamAvans)}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-5">
            <div className="text-sm text-gray-500">Net Ücret</div>
            <div className="text-4xl font-bold text-green-600 mt-1">₺{formatMoney(totals.netToplam)}</div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden print:shadow-none print:border print:border-gray-200">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-600 border-t-transparent" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[920px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">#</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Öğretmen Adı</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Telefon</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">E-posta</th>
                    <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Ders</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Brüt Ücret</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Avans</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Net Ücret</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredTeachers.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-10 text-gray-500">
                        Gösterilecek veri bulunamadı
                      </td>
                    </tr>
                  ) : (
                    filteredTeachers.map((teacher, index) => {
                      const data = financialData[teacher.id] || {};
                      const dersSayisi = Number(data.dersSayisi) || 0;
                      const brutUcret = safeParseFloat(data.brutToplam);
                      const avans = safeParseFloat(data.toplamAvans);
                      const netUcret = safeParseFloat(data.netToplam);

                      return (
                        <tr key={teacher.id} className="hover:bg-gray-50">
                          <td className="px-5 py-4 text-sm text-gray-500">{index + 1}</td>
                          <td className="px-5 py-4 text-sm font-semibold text-gray-900">
                            <button
                              onClick={() => openTeacherDetail(teacher)}
                              className="inline-flex items-center gap-2 hover:text-blue-600"
                            >
                              {teacher.adi}
                              <FiEye className="w-4 h-4 text-gray-400" />
                            </button>
                          </td>
                          <td className="px-5 py-4 text-sm text-gray-600">{teacher.telefon || '-'}</td>
                          <td className="px-5 py-4 text-sm text-gray-600 max-w-[240px] truncate" title={teacher.e_posta || ''}>
                            {teacher.e_posta || '-'}
                          </td>
                          <td className="px-5 py-4 text-center">
                            {dersSayisi > 0 ? (
                              <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-semibold bg-violet-100 text-violet-700">
                                {dersSayisi}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="px-5 py-4 text-right text-sm text-gray-800">₺{formatMoney(brutUcret)}</td>
                          <td className="px-5 py-4 text-right text-sm text-orange-600">₺{formatMoney(avans)}</td>
                          <td className="px-5 py-4 text-right text-sm font-semibold text-green-600">₺{formatMoney(netUcret)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-4 text-center text-sm text-gray-500 print:hidden">
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 font-medium mr-2">
            {getPeriodTag()} Raporu
          </span>
          <span>
            Rapor Tarihi: {new Date().toLocaleDateString('tr-TR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </span>
        </div>
      </div>

      <TeacherDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedTeacher(null);
        }}
        teacher={selectedTeacher}
        period={periodFilter}
        month={selectedMonth}
        year={selectedYear}
      />

    </div>
  );
};

export default TeacherReportPage;
